import React, {
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
  useCallback
} from "react";
import { FiChevronLeft } from "react-icons/fi";
import apiClient from "./api"; // API 클라이언트


export default function DungeonScreen({ onBack, user }) {
  const [selectedDungeon, setSelectedDungeon] = useState(null);
  const [groundOffset, setGroundOffset] = useState(0);
  const bgRef = useRef(null);
  
  const [stage, setStage] = useState("loading");
  const [message, setMessage] = useState("");
  const [error, setError] = useState(null);
  const battleResult = useRef(null);
  const timeoutIds = useRef([]);

  const fetchAndSelectDungeon = useCallback(async () => {
    try {
      setStage("loading");
      setSelectedDungeon(null);
      const response = await apiClient.get("/dungeons");
      const allDungeons = response.data.result;
      const enterableDungeons = allDungeons.filter(d => d.userStatus.canEnter);

      if (enterableDungeons.length > 0) {
        const randomIndex = Math.floor(Math.random() * enterableDungeons.length);
        setSelectedDungeon(enterableDungeons[randomIndex]);
        setStage("entry");
      } else {
        setStage("no_dungeon");
      }
      setError(null);
    } catch (err) {
      setError("던전 정보를 불러오는 데 실패했습니다.");
      console.error(err);
      setStage("error");
    }
  }, []);

   
  // ① 렌더된 배경 높이에서 땅 위치 비율 → 픽셀로 계산
  useLayoutEffect(() => {
    if (!bgRef.current || !selectedDungeon) return;
    const h = bgRef.current.getBoundingClientRect().height;
    const offset = selectedDungeon.groundYRatio * h;
    console.log("🌱 groundOffset 계산값:", offset, "배경 높이:", h);
    setGroundOffset(offset);
  }, [selectedDungeon]);
  

  useEffect(() => {
    fetchAndSelectDungeon();
  }, [fetchAndSelectDungeon]);

  const clearTimers = () => {
    timeoutIds.current.forEach(clearTimeout);
    timeoutIds.current = [];
  };

  const startBattle = async () => {
    if (!selectedDungeon) return;

    try {
      const response = await apiClient.post(`/dungeons/${selectedDungeon.id}/enter`);
      battleResult.current = response.data.result;

     // 2. UI 연출을 시작합니다.
      setStage("battle");
      setMessage("던전 탐험 중…");          // 기본 연출 시작 메시지

            timeoutIds.current.push(
                setTimeout(() => {
                  setMessage(
                    battleResult.current.isCritical
                      ? "보스몹을 일격으로 처치!"   // 특별 텍스트
                      : "보스몹 처치!"              // 기본 텍스트
                  );
                }, 5000)
             );

      timeoutIds.current.push(setTimeout(() => {
        setMessage(`EXP +${battleResult.current.expGained}`);
        setStage("reward");
      }, 10000));
    } catch (err) {
      alert(err.response?.data?.message || "던전 입장에 실패했습니다.");
      fetchAndSelectDungeon();
    }
  };

  useEffect(() => {
    if (stage !== "reward") return;
    const id = setTimeout(() => {
      fetchAndSelectDungeon();
    }, 3000);
    return () => clearTimeout(id);
  }, [stage, fetchAndSelectDungeon]);

  useEffect(() => {
    return () => clearTimers();
  }, []);

  const getBackgroundImage = () => {
    if (stage === 'reward' && selectedDungeon) {
      return `url(${selectedDungeon.rewardImageUrl})`;
    }
    if (selectedDungeon) {
      return `url(${selectedDungeon.imageUrl})`;
    }
    return 'none';
  };
  
  const renderContent = () => {
    if (stage === 'loading') {
      return <p>오늘의 던전을 탐색하는 중...</p>;
    }
    if (stage === 'error') {
      return <p style={{ color: 'red' }}>{error}</p>;
    }
    if (stage === 'no_dungeon' || !selectedDungeon) {
      return <p>오늘 입장할 수 있는 던전이 없습니다.</p>;
    }
    
    if (stage === 'battle' || stage === 'reward') {
      return (
        <>
          <h2 style={{ fontSize: 28, fontWeight: 800 }}>{message}</h2>
          
          {/* 캐릭터 이미지를 땅 위치에 맞춰서 배치 */}            
          <img
              src={`/assets/${user.profile.characterType}.png`}
              alt="player"
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                bottom: groundOffset,     // ← groundOffset 반영
                width: 120,
                objectFit: "contain",
              }}
            />
          {stage === "reward" && battleResult.current && (
            <div style={{
                marginTop: 60, // 보상 카드와 위쪽 메시지 사이의 간격
                width: 260, minHeight: 160, background: "#222", border: "2px solid #d2b46c",
                borderRadius: 20, color: "#fff", fontSize: 28, fontWeight: 700,
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12,
            }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <img src="/assets/coin_icon.png" alt="coin" style={{ width: 28 }} />
                {battleResult.current.expGained}
              </span>
              <small style={{ fontSize: 14 }}>{battleResult.current.isCritical ? "(2배 보상!)" : "기본 보상"}</small>
              {battleResult.current.didLevelUp && <small style={{ fontSize: 16, color: '#ffd700' }}>LEVEL UP!</small>}
            </div>
          )}
        </>
      );
    }

    // stage === 'entry'
    return (
       <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: 16, color: '#aaa', padding: '0 20px', marginBottom: 40 }}>{selectedDungeon.description}</p>
          {selectedDungeon.userStatus.triesLeft > 0 ? (
            <button onClick={startBattle} style={{
                width: 200, height: 70, borderRadius: 16, background: "#4d2a00",
                border: "2px solid #d2b46c", color: "#fff", fontSize: 24, fontWeight: 700, cursor: "pointer",
            }}>
              입장하기
            </button>
          ) : (
             <p style={{fontSize: 18}}>오늘 남은 입장 횟수가 없습니다</p>
          )}
          <p style={{ marginTop: 12, fontSize: 14 }}>남은 횟수: {selectedDungeon.userStatus.triesLeft}</p>
        </div>
    );
  };
  
  return (
    <div
      ref={bgRef}
      style={{
        position: "relative", 
        width: 393,
        /* 고정된 비율 유지: 실제 배경 이미지의 width/height 비율로 교체하세요 */
        aspectRatio: "393 / 844",
        /* 만약 구 브라우저 지원이 필요하면 아래 padding-bottom hack 을 쓰세요:
        height: 0,
        paddingBottom: `${844 / 393 * 100}%`,
        */
        margin: "0 auto",
        backgroundColor: "#000",
        backgroundImage: getBackgroundImage(),
        backgroundSize: "cover",
        backgroundPosition: "center",
        overflow: "hidden",      /* 좌우 잘라내기 */
        fontFamily: "'Pretendard', sans-serif",
        display: "flex",
        flexDirection: "column",
        color: "#fff",
      }}
    >
      <header style={{ display: "flex", alignItems: "center", height: 56, padding: "0 12px", borderBottom: '1px solid #333' }}>
        <button onClick={onBack} style={{ background: "none", border: "none", fontSize: 24, color: "#fff", cursor: "pointer" }}>
          <FiChevronLeft />
        </button>
        <h1 style={{ flex: 1, textAlign: "center", fontSize: 20, fontWeight: 700, margin: 0 }}>
          {selectedDungeon ? selectedDungeon.name : '던전'}
        </h1>
        <div style={{ width: 40 }} />
      </header>

      {/* --- 메인 컨텐츠 영역 --- */}
      <div style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: 'flex-start', // 1. 중앙 정렬에서 상단 정렬로 변경
        paddingTop: 60,               // 2. 위쪽에 여백 추가
        textAlign: 'center',          // 3. 내부 텍스트들 중앙 정렬
      }}>
        {renderContent()}
      </div>
    </div>
  );
}